import { compact, isEmpty } from 'lodash';
import { useCallback } from 'react';

import { useAsyncTypeaheadProps } from '@/components/form/baseInputs/BackendTypeaheadSelectInput/hooks/useAsyncTypeaheadProps';
import { DocumentType } from '@/types/schema';

import {
  SearchDocumentsForPromptTypeaheadQuery,
  useSearchDocumentsForPromptTypeaheadQuery,
} from '../graphql/AdminPromptIterationPage.generated';

type DocNode = NonNullable<
  NonNullable<
    NonNullable<SearchDocumentsForPromptTypeaheadQuery['queryData']>['edges']
  >[number]
>['node'];

export function useDocumentsTypeaheadForPrompt(
  documentType: DocumentType,
  selectedDocumentId?: string
) {
  const searchTermToVariables = useCallback(
    (searchTerm: string) => ({
      optionsLike: {
        and: compact([
          { type: documentType },
          searchTerm && { nameContainsFold: searchTerm },
        ]),
      },
      first: 50,
    }),
    [documentType]
  );

  const getSelectedOptionVariables = useCallback(
    () => ({
      selectedOptionsLike: {
        idIn:
          selectedDocumentId && !isEmpty(selectedDocumentId)
            ? [selectedDocumentId]
            : [''],
      },
    }),
    [selectedDocumentId]
  );

  const toOptions = useCallback((data: DocNode[]) => {
    return compact(
      data.map((document) => {
        if (!document) return null;

        return {
          value: document.id,
          display: document.name,
          groupName: document.household.displayName,
        };
      })
    );
  }, []);

  const [typeaheadProps] = useAsyncTypeaheadProps(
    useSearchDocumentsForPromptTypeaheadQuery,
    {
      searchTermToVariables,
      getSelectedOptionVariables,
      toOptions,
      pageSize: 50,
      skipAutocompleteResetClear: true,
      unselectedGroupLabel: 'Matching documents',
      fetchPolicy: 'no-cache',
      // skip: !documentType,
    }
  );

  return { typeaheadProps };
}
